import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Project Module Service Stores
 * Updated to match backend api-types
 */
export const projectStores: ServiceStoreConfig[] = [
  {
    name: "look-ahead-schedule",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.lookAheadScheduleStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalSchedules = entities.length;
          const thisWeek = entities.filter((schedule: Record<string, unknown>) => {
            const weekStart = new Date(schedule.weekStartingDate as string);
            const now = new Date();
            const weekFromNow = new Date();
            weekFromNow.setDate(now.getDate() + 7);
            return weekStart >= now && weekStart <= weekFromNow;
          }).length;
          const totalCrewSize = entities.reduce((sum: number, schedule: Record<string, unknown>) => {
            return sum + ((schedule.crewSize as number) || 0);
          }, 0);
          const safetyConcerns = entities.filter(
            (schedule: Record<string, unknown>) => schedule.safetyConcerns && (schedule.safetyConcerns as string).length > 0
          ).length;

          return [
            {
              title: "Total Schedules",
              value: () => totalSchedules.toString(),
              valueColor: "primary.main",
            },
            {
              title: "This Week",
              value: () => thisWeek.toString(),
              valueColor: "info.main",
            },
            {
              title: "Total Crew Size",
              value: () => totalCrewSize.toString(),
              valueColor: "success.main",
            },
            {
              title: "Safety Concerns",
              value: () => safetyConcerns.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "project-checklist",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectChecklistStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalTasks = entities.length;
          const pending = entities.filter(
            (task: Record<string, unknown>) => task.status === "pending"
          ).length;
          const completed = entities.filter(
            (task: Record<string, unknown>) => task.status === "completed"
          ).length;
          const overdue = entities.filter((task: Record<string, unknown>) => {
            if (!task.dueDate || task.status === "completed") return false;
            const dueDate = new Date(task.dueDate as string);
            const now = new Date();
            return dueDate < now;
          }).length;

          return [
            {
              title: "Total Tasks",
              value: () => totalTasks.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Pending",
              value: () => pending.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Completed",
              value: () => completed.toString(),
              valueColor: "success.main",
            },
            {
              title: "Overdue",
              value: () => overdue.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "project-labor",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectLaborStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalHours = entities.reduce((sum: number, labor: Record<string, unknown>) => {
            return sum + ((labor.hoursWorked as number) || 0);
          }, 0);
          const totalCost = entities.reduce((sum: number, labor: Record<string, unknown>) => {
            return sum + ((labor.totalCost as number) || 0);
          }, 0);
          const overtimeHours = entities
            .filter((labor: Record<string, unknown>) => labor.overtime === true)
            .reduce((sum: number, labor: Record<string, unknown>) => {
              return sum + ((labor.hoursWorked as number) || 0);
            }, 0);
          const uniqueEmployees = new Set(
            entities.map((labor: Record<string, unknown>) => labor.employeeId)
          ).size;

          return [
            {
              title: "Total Hours",
              value: () => (totalHours as number).toFixed(1),
              valueColor: "primary.main",
            },
            {
              title: "Total Cost",
              value: () => `$${(totalCost as number).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              valueColor: "info.main",
            },
            {
              title: "Overtime Hours",
              value: () => (overtimeHours as number).toFixed(1),
              valueColor: "warning.main",
            },
            {
              title: "Unique Employees",
              value: () => uniqueEmployees.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "project-portfolio-summary",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectPortfolioSummaryStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const latestSummary = entities.length > 0 ? (entities[0] as Record<string, unknown>) : null;
          const activeProjects = latestSummary?.activeProjectsCount || 0;
          const backlogValue = latestSummary?.totalBacklogValue || 0;
          const pipelineValue = latestSummary?.pipelineValue || 0;
          const winRate = latestSummary?.winRatePercent || 0;

          return [
            {
              title: "Active Projects",
              value: () => (activeProjects as number).toString(),
              valueColor: "primary.main",
            },
            {
              title: "Backlog Value",
              value: () => `$${((backlogValue as number) / 1000000).toFixed(1)}M`,
              valueColor: "success.main",
            },
            {
              title: "Pipeline Value",
              value: () => `$${((pipelineValue as number) / 1000000).toFixed(1)}M`,
              valueColor: "info.main",
            },
            {
              title: "Win Rate",
              value: () => `${(winRate as number).toFixed(1)}%`,
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "projects",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.projectsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalProjects = entities.length;
          const activeProjects = entities.filter(
            (project: Record<string, unknown>) => project.status === "active" || project.status === "in_progress"
          ).length;
          const totalContractValue = entities.reduce((sum: number, project: Record<string, unknown>) => {
            return sum + ((project.contractValue as number) || 0);
          }, 0);
          const avgProjectValue = totalProjects > 0 ? totalContractValue / totalProjects : 0;

          return [
            {
              title: "Total Projects",
              value: () => totalProjects.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active Projects",
              value: () => activeProjects.toString(),
              valueColor: "success.main",
            },
            {
              title: "Total Contract Value",
              value: () => `$${((totalContractValue as number) / 1000000).toFixed(2)}M`,
              valueColor: "info.main",
            },
            {
              title: "Avg Project Value",
              value: () => `$${((avgProjectValue as number) / 1000).toFixed(0)}K`,
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "rfis",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.rfisStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalRFIs = entities.length;
          const open = entities.filter(
            (rfi: Record<string, unknown>) => rfi.status === "open" || rfi.status === "pending"
          ).length;
          const answered = entities.filter(
            (rfi: Record<string, unknown>) => rfi.status === "answered" || rfi.status === "closed"
          ).length;
          const thisMonth = entities.filter((rfi: Record<string, unknown>) => {
            const submittedDate = new Date(rfi.dateSubmitted as string);
            const now = new Date();
            return (
              submittedDate.getMonth() === now.getMonth() &&
              submittedDate.getFullYear() === now.getFullYear()
            );
          }).length;

          return [
            {
              title: "Total RFIs",
              value: () => totalRFIs.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Open",
              value: () => open.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Answered",
              value: () => answered.toString(),
              valueColor: "success.main",
            },
            {
              title: "This Month",
              value: () => thisMonth.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "submittals",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.submittalsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalSubmittals = entities.length;
          const pendingReview = entities.filter(
            (submittal: Record<string, unknown>) =>
              submittal.status === "submitted" || submittal.status === "in_review"
          ).length;
          const approved = entities.filter(
            (submittal: Record<string, unknown>) => submittal.status === "approved"
          ).length;
          const rejected = entities.filter(
            (submittal: Record<string, unknown>) =>
              submittal.status === "rejected" || submittal.status === "needs_revision"
          ).length;

          return [
            {
              title: "Total Submittals",
              value: () => totalSubmittals.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Pending Review",
              value: () => pendingReview.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Approved",
              value: () => approved.toString(),
              valueColor: "success.main",
            },
            {
              title: "Rejected",
              value: () => rejected.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];
